import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getNavContextForPath } from '../features/permissions/navLabel';
import type {
  PageShellConfig,
  PageShellContextValue,
  PageShellResolved,
} from './pageShellTypes';

const PageShellContext = createContext<PageShellContextValue | null>(null);

function buildRouteBreadcrumb(
  groupLabel: string | null,
  pageLabel: string | null,
): PageShellResolved['breadcrumb'] {
  if (!pageLabel) {
    return [];
  }
  if (groupLabel) {
    return [{ title: groupLabel }, { title: pageLabel }];
  }
  return [{ title: pageLabel }];
}

function resolvePageShell(
  pathname: string,
  override: PageShellConfig | null,
): PageShellResolved {
  const { label, groupLabel } = getNavContextForPath(pathname);
  const routeBreadcrumb = buildRouteBreadcrumb(groupLabel, label);

  const title =
    override && 'title' in override
      ? override.title ?? null
      : label;

  const breadcrumb =
    override && 'breadcrumb' in override
      ? override.breadcrumb ?? []
      : routeBreadcrumb;

  return {
    title,
    subtitle: override?.subtitle,
    actions: override?.actions,
    filters: override?.filters,
    breadcrumb,
    hideChrome: override?.hideChrome ?? false,
  };
}

export function usePageShellContext(): PageShellContextValue {
  const ctx = useContext(PageShellContext);
  if (ctx === null) {
    throw new Error('usePageShellContext must be used within PageShellProvider');
  }
  return ctx;
}

interface PageShellProviderProps {
  children: React.ReactNode;
}

export const PageShellProvider: React.FC<PageShellProviderProps> = ({ children }) => {
  const { pathname } = useLocation();
  const [override, setOverride] = useState<PageShellConfig | null>(null);

  const setPageShell = useCallback((config: PageShellConfig) => {
    setOverride(config);
  }, []);

  const clearPageShell = useCallback(() => {
    setOverride(null);
  }, []);

  const resolved = useMemo(
    () => resolvePageShell(pathname, override),
    [pathname, override],
  );

  const contextValue = useMemo(
    () => ({ setPageShell, clearPageShell }),
    [setPageShell, clearPageShell],
  );

  return (
    <PageShellContext.Provider value={contextValue}>
      <PageShellResolvedContext.Provider value={resolved}>
        {children}
      </PageShellResolvedContext.Provider>
    </PageShellContext.Provider>
  );
};

const PageShellResolvedContext = createContext<PageShellResolved | null>(null);

export function usePageShellResolved(): PageShellResolved {
  const resolved = useContext(PageShellResolvedContext);
  if (resolved === null) {
    throw new Error('usePageShellResolved must be used within PageShellProvider');
  }
  return resolved;
}
